<!-- src/components/SettingsPanel.svelte -->
<script>
  import { agentStore, uiStore } from '$lib/stores'

  let activeTab = 'providers'

  const tabs = [
    { id: 'providers', label: 'Providers', icon: '⚡' },
    { id: 'groups', label: 'Groups', icon: '📁' },
    { id: 'theme', label: 'Theme', icon: '◐' },
    { id: 'resources', label: 'Resources', icon: '⚙' },
    { id: 'sync', label: 'Sync', icon: '↕' },
  ]

  const accentColors = ['purple', 'blue', 'green', 'gold', 'pink']

  function setTheme(theme) {
    uiStore.update(s => ({ ...s, theme }))
  }

  function setAccent(color) {
    uiStore.update(s => ({ ...s, accentColor: color }))
  }
</script>

<div class="settings-panel glass-panel">
  <h2 class="panel-title">Settings</h2>
  
  <div class="settings-tabs">
    {#each tabs as tab}
      <button
        class="tab-btn"
        class:active={activeTab === tab.id}
        on:click={() => activeTab = tab.id}
      >
        <span>{tab.icon}</span>
        {tab.label}
      </button>
    {/each}
  </div>

  <div class="settings-content">
    {#if activeTab === 'providers'}
      <div class="settings-section">
        <h3>OmniRouter Providers</h3>
        <p class="section-desc">Configure provider endpoints and preferences</p>
        <div class="provider-list">
          {#each $agentStore.providers as provider}
            <div class="provider-row">
              <div class="provider-info">
                <span class="provider-name">{provider.name}</span>
                <span class="provider-family">{provider.family}</span>
              </div>
              <div class="provider-meta">
                {#if provider.api_key_required}
                  <span class="tag tag-warn">Key Required</span>
                {/if}
                <span class="tag">{provider.speed}</span>
                <span class="status-dot" class:active={provider.available}></span>
              </div>
            </div>
          {/each}
        </div>
      </div>

    {:else if activeTab === 'groups'}
      <div class="settings-section">
        <h3>Grea Office Groups</h3>
        <p class="section-desc">Manage agent groups and resource limits</p>
        <div class="group-settings-list">
          {#each $agentStore.groups as group}
            <div class="group-settings-row">
              <div class="group-identity">
                <span class="group-icon" style="color: {group.color}">{group.icon}</span>
                <div>
                  <span class="group-name">{group.name}</span>
                  <span class="group-desc">{group.description}</span>
                </div>
              </div>
              <div class="group-controls">
                <label>Max Agents</label>
                <input type="number" value={group.max_agents} min="1" max="32" />
              </div>
            </div>
          {/each}
        </div>
      </div>

    {:else if activeTab === 'theme'}
      <div class="settings-section">
        <h3>Appearance</h3>
        <div class="theme-options">
          <div class="option-group">
            <label>Mode</label>
            <div class="toggle-group">
              <button class="toggle-btn" class:active={$uiStore.theme === 'dark'} on:click={() => setTheme('dark')}>
                ◐ Dark
              </button>
              <button class="toggle-btn" class:active={$uiStore.theme === 'light'} on:click={() => setTheme('light')}>
                ◑ Light
              </button>
            </div>
          </div>
          <div class="option-group">
            <label>Accent Color</label>
            <div class="accent-swatches">
              {#each accentColors as color}
                <button
                  class="swatch"
                  class:active={$uiStore.accentColor === color}
                  style="--swatch-color: {color === 'purple' ? '#7c5cff' : color === 'blue' ? '#60a5fa' : color === 'green' ? '#4ade80' : color === 'gold' ? '#fbbf24' : '#f472b6'}"
                  on:click={() => setAccent(color)}
                ></button>
              {/each}
            </div>
          </div>
        </div>
      </div>

    {:else if activeTab === 'resources'}
      <div class="settings-section">
        <h3>Resource Limits</h3>
        <p class="section-desc">Configure CPU, memory, and scaling thresholds</p>
        <div class="resource-sliders">
          <div class="slider-group">
            <label>Max Local Agents</label>
            <input type="range" min="1" max="50" value="20" />
            <span class="slider-value">20</span>
          </div>
          <div class="slider-group">
            <label>CPU Threshold for Modal (%)</label>
            <input type="range" min="50" max="95" value="80" />
            <span class="slider-value">80%</span>
          </div>
          <div class="slider-group">
            <label>Memory Threshold for Modal (%)</label>
            <input type="range" min="50" max="95" value="85" />
            <span class="slider-value">85%</span>
          </div>
        </div>
      </div>

    {:else if activeTab === 'sync'}
      <div class="settings-section">
        <h3>Turso Sync</h3>
        <p class="section-desc">Cloud synchronization settings</p>
        <div class="sync-options">
          <div class="sync-row">
            <label>Turso URL</label>
            <input type="text" placeholder="libsql://your-db.turso.io" />
          </div>
          <div class="sync-row">
            <label>Auth Token</label>
            <input type="password" placeholder="••••••••••••" />
          </div>
          <div class="sync-row">
            <label>Sync Interval (seconds)</label>
            <input type="number" value="30" min="5" />
          </div>
          <button class="btn-primary">Save & Test Connection</button>
        </div>
      </div>
    {/if}
  </div>
</div>

<style>
  .settings-panel {
    display: flex;
    flex-direction: column;
    gap: 20px;
    max-width: 900px;
    margin: 0 auto;
  }

  .panel-title {
    font-size: 20px;
    font-weight: 700;
    color: var(--text-primary);
  }

  .settings-tabs {
    display: flex;
    gap: 4px;
    padding: 4px;
    background: var(--glass-bg);
    border-radius: var(--radius-md);
    border: 1px solid var(--glass-border);
  }

  .tab-btn {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 8px 16px;
    border: none;
    background: transparent;
    color: var(--text-secondary);
    border-radius: var(--radius-sm);
    cursor: pointer;
    font-size: 13px;
    font-weight: 500;
    transition: all var(--transition-fast);
  }

  .tab-btn:hover {
    color: var(--text-primary);
  }

  .tab-btn.active {
    background: var(--accent-primary);
    color: white;
  }

  .settings-content {
    min-height: 300px;
  }

  .settings-section h3 {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 4px;
    color: var(--text-primary);
  }

  .section-desc {
    font-size: 13px;
    color: var(--text-muted);
    margin-bottom: 20px;
  }

  .provider-list, .group-settings-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .provider-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    background: var(--glass-bg);
    border: 1px solid var(--glass-border);
    border-radius: var(--radius-md);
  }

  .provider-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .provider-name {
    font-weight: 600;
    font-size: 14px;
    color: var(--text-primary);
  }

  .provider-family {
    font-size: 12px;
    color: var(--text-muted);
  }

  .provider-meta {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .tag {
    font-size: 11px;
    padding: 3px 8px;
    background: rgba(124, 92, 255, 0.1);
    color: var(--accent-primary);
    border-radius: 10px;
  }

  .tag-warn {
    background: rgba(251, 191, 36, 0.1);
    color: #fbbf24;
  }

  .group-settings-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px;
    background: var(--glass-bg);
    border: 1px solid var(--glass-border);
    border-radius: var(--radius-md);
    margin-bottom: 8px;
  }

  .group-identity {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .group-icon {
    font-size: 28px;
  }

  .group-identity .group-name {
    display: block;
    font-weight: 600;
    color: var(--text-primary);
    text-transform: capitalize;
  }

  .group-identity .group-desc {
    display: block;
    font-size: 12px;
    color: var(--text-muted);
  }

  .group-controls {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .group-controls label {
    font-size: 12px;
    color: var(--text-muted);
  }

  .group-controls input {
    width: 60px;
    padding: 6px 10px;
    background: var(--glass-bg);
    border: 1px solid var(--glass-border);
    border-radius: var(--radius-sm);
    color: var(--text-primary);
    font-size: 13px;
  }

  .theme-options {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  .option-group label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: var(--text-secondary);
    margin-bottom: 10px;
  }

  .toggle-group {
    display: flex;
    gap: 8px;
  }

  .toggle-btn {
    flex: 1;
    padding: 12px 20px;
    border: 1px solid var(--glass-border);
    background: var(--glass-bg);
    color: var(--text-secondary);
    border-radius: var(--radius-md);
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    transition: all var(--transition-fast);
  }

  .toggle-btn.active {
    background: var(--accent-primary);
    border-color: var(--accent-primary);
    color: white;
  }

  .accent-swatches {
    display: flex;
    gap: 12px;
  }

  .swatch {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: 2px solid var(--glass-border);
    background: var(--swatch-color);
    cursor: pointer;
    transition: all var(--transition-fast);
  }

  .swatch.active {
    border-color: white;
    transform: scale(1.15);
    box-shadow: 0 0 12px var(--swatch-color);
  }

  .resource-sliders {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .slider-group {
    display: grid;
    grid-template-columns: 200px 1fr 50px;
    align-items: center;
    gap: 16px;
  }

  .slider-group label {
    font-size: 13px;
    color: var(--text-secondary);
  }

  .slider-group input[type="range"] {
    width: 100%;
    accent-color: var(--accent-primary);
  }

  .slider-value {
    font-size: 13px;
    color: var(--text-primary);
    text-align: right;
    font-variant-numeric: tabular-nums;
  }

  .sync-options {
    display: flex;
    flex-direction: column;
    gap: 16px;
    max-width: 400px;
  }

  .sync-row {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .sync-row label {
    font-size: 13px;
    font-weight: 500;
    color: var(--text-secondary);
  }

  .sync-row input {
    padding: 10px 14px;
    background: var(--glass-bg);
    border: 1px solid var(--glass-border);
    border-radius: var(--radius-md);
    color: var(--text-primary);
    font-size: 14px;
  }

  .btn-primary {
    padding: 12px 24px;
    background: var(--accent-primary);
    color: white;
    border: none;
    border-radius: var(--radius-md);
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all var(--transition-fast);
    margin-top: 8px;
  }

  .btn-primary:hover {
    background: #6a4fe0;
    transform: translateY(-1px);
  }
</style>